import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import BrandHeader from "@/components/BrandHeader";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Copy, Loader2, CheckCircle2, XCircle, CreditCard, ExternalLink, AlertTriangle, UserPlus } from "lucide-react";

export default function OrderWaiting() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let mounted = true;
    let interval;
    const fetchOrder = async () => {
      try {
        const { data } = await api.get(`/orders/${id}`);
        if (!mounted) return;
        setOrder(data);
        if (data.status === "confirmed") {
          navigate(`/success/${id}`, { replace: true });
        } else if (data.status === "cancelled") {
          setError("Esta orden fue cancelada.");
          clearInterval(interval);
        }
      } catch (e) {
        if (mounted) setError("Orden no encontrada");
      }
    };
    fetchOrder();
    interval = setInterval(fetchOrder, 4000);
    return () => {
      mounted = false;
      clearInterval(interval);
    };
  }, [id, navigate]);

  const copy = (val, label) => {
    navigator.clipboard.writeText(String(val));
    toast.success(`${label} copiado`);
  };

  const downloadZelleContact = () => {
    const zelleEmail = order?.zelle_email || "mdlaiv1990@gmail.com";
    // vCard 3.0 — universally supported by iOS Contacts and Android
    const vcard = [
      "BEGIN:VCARD",
      "VERSION:3.0",
      "FN:JOSENGINER ONE LLC (Zelle B94)",
      "N:JOSENGINER ONE LLC;;;;",
      "ORG:JOSENGINER ONE LLC",
      `EMAIL;TYPE=INTERNET;TYPE=WORK:${zelleEmail}`,
      "NOTE:Contacto Zelle — Portal Gasolina B94",
      "END:VCARD",
      "",
    ].join("\r\n");
    const blob = new Blob([vcard], { type: "text/vcard;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "JOSENGINER-ONE-LLC-Zelle.vcf";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
    toast.success("Contacto Zelle listo. Abre el archivo para agregarlo.");
  };

  if (error) {
    return (
      <div className="min-h-screen bg-zinc-950 text-white">
        <BrandHeader />
        <div className="max-w-2xl mx-auto px-6 py-24 text-center">
          <XCircle className="w-16 h-16 text-red-500 mx-auto mb-6" />
          <h1 className="font-display text-4xl font-bold mb-4">{error}</h1>
          <Button onClick={() => navigate("/")} className="bg-yellow-400 text-black hover:bg-yellow-300 font-bold">
            Volver al inicio
          </Button>
        </div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="min-h-screen bg-zinc-950 text-white flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-yellow-400" />
      </div>
    );
  }

  const isStripe = order.payment_method === "stripe";

  const copyCode = () => {
    if (!order.code) return;
    navigator.clipboard.writeText(order.code);
    toast.success("Código copiado");
  };

  return (
    <div className="min-h-screen bg-zinc-950 text-white relative">
      <BrandHeader />
      <div className="absolute inset-0 grid-lines opacity-30 pointer-events-none" aria-hidden />

      <main className="relative z-10 max-w-5xl mx-auto px-4 md:px-12 py-8 md:py-16">
        {/* Reserved code banner — visible while payment is pending */}
        {order.code && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35 }}
            className="mb-8 rounded-2xl border border-yellow-400/30 bg-gradient-to-br from-yellow-400/10 via-zinc-900/60 to-zinc-900/60 p-6 md:p-8"
            data-testid="reserved-code-banner"
          >
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-5">
              <div className="min-w-0">
                <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full bg-amber-500/15 border border-amber-500/40 mb-3">
                  <AlertTriangle className="w-3 h-3 text-amber-300" />
                  <span
                    className="text-[10px] uppercase tracking-widest font-mono-display text-amber-200"
                    data-testid="pending-status-badge"
                  >
                    Pago pendiente · No autorizado para despacho
                  </span>
                </div>
                <div className="text-[11px] uppercase tracking-[0.3em] font-mono-display text-zinc-500 mb-1">
                  Tu código reservado
                </div>
                <div
                  className="font-mono-display font-black text-5xl md:text-6xl tracking-[0.2em] text-yellow-400 code-glow"
                  data-testid="reserved-code"
                >
                  {order.code}
                </div>
                <p className="text-xs md:text-sm text-zinc-400 mt-3 max-w-md">
                  Guarda este código como referencia. Se activará para despacho automáticamente cuando confirmemos tu pago.
                </p>
              </div>
              <Button
                onClick={copyCode}
                data-testid="copy-reserved-code-btn"
                className="bg-yellow-400 hover:bg-yellow-300 text-black font-bold h-11 px-5 shrink-0"
              >
                <Copy className="w-4 h-4 mr-2" /> Copiar código
              </Button>
            </div>
          </motion.div>
        )}

        <div className="grid md:grid-cols-5 gap-6 md:gap-8">
          {/* LEFT: Payment instructions */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="md:col-span-3 glass rounded-xl p-8 md:p-10 relative overflow-hidden"
            data-testid={isStripe ? "stripe-panel" : "zelle-panel"}
          >
            <div className="absolute -top-8 -right-8 w-32 h-32 rounded-full bg-yellow-400/10 blur-3xl" />
            <div className="relative">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-yellow-400/10 border border-yellow-400/30 mb-4">
                <span className="w-1.5 h-1.5 rounded-full bg-yellow-400 pulse-dot" />
                <span className="text-xs uppercase tracking-widest font-mono-display text-yellow-300">
                  Esperando pago
                </span>
              </div>

              {isStripe ? (
                <>
                  <h1 className="font-display font-black text-4xl md:text-5xl leading-tight mb-2">
                    Paga con tarjeta<br />
                    <span className="text-yellow-400">${order.total.toFixed(2)}</span>
                  </h1>
                  <p className="text-zinc-400 mb-6">
                    Se abrió una ventana segura de Stripe con el monto exacto ya cargado.
                    Si se cerró, ábrela de nuevo con el botón de abajo.
                  </p>

                  <a
                    href={order.stripe_checkout_url || "#"}
                    onClick={(e) => {
                      // On mobile, use same-tab redirect so pop-up blockers can't intercept.
                      if (order.stripe_checkout_url && /Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent)) {
                        e.preventDefault();
                        window.location.href = order.stripe_checkout_url;
                      }
                    }}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="stripe-open-link"
                    className="inline-flex items-center gap-2 bg-yellow-400 hover:bg-yellow-300 text-black font-bold px-5 py-3 rounded-md transition-transform duration-150 hover:-translate-y-0.5"
                  >
                    <CreditCard className="w-4 h-4" />
                    Abrir Stripe para pagar ${order.total.toFixed(2)}
                    <ExternalLink className="w-3.5 h-3.5" />
                  </a>

                  <div className="mt-6 border border-white/10 bg-zinc-900/40 rounded-lg p-4 text-sm text-zinc-400 space-y-2">
                    <div className="flex items-center gap-2 text-emerald-300 text-xs uppercase tracking-widest font-mono-display">
                      <CheckCircle2 className="w-3.5 h-3.5" /> Confirmación automática
                    </div>
                    <p>
                      En cuanto Stripe confirme tu pago, generamos el código de 6 letras
                      automáticamente en esta pantalla — <b className="text-white">no la cierres</b>.
                    </p>
                  </div>
                </>
              ) : (
                <>
                  <h1 className="font-display font-black text-4xl md:text-5xl leading-tight mb-2">
                    Envía <span className="text-yellow-400">${order.total.toFixed(2)}</span> por Zelle
                  </h1>
                  <p className="text-zinc-400 mb-8">
                    Realiza el pago exacto para que el sistema pueda emparejarlo automáticamente con tu orden.
                  </p>

                  <div className="space-y-4">
                    <CopyRow
                      label="Enviar Zelle a"
                      value={order.zelle_email}
                      onCopy={() => copy(order.zelle_email, "Email Zelle")}
                      testid="zelle-email"
                      big
                    />
                    <div
                      data-testid="zelle-account-holder"
                      className="-mt-2 border border-white/10 bg-zinc-900/40 rounded-lg px-4 py-3 flex items-center justify-between"
                    >
                      <div>
                        <div className="text-[11px] uppercase tracking-[0.25em] font-mono-display text-zinc-500">
                          Registrado bajo
                        </div>
                        <div className="font-mono-display text-yellow-300 text-sm mt-0.5">
                          JOSENGINER ONE LLC
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => copy("JOSENGINER ONE LLC", "Nombre")}
                        data-testid="copy-account-holder-btn"
                        className="text-yellow-400 hover:text-yellow-300 hover:bg-yellow-400/10 h-8"
                      >
                        <Copy className="w-3.5 h-3.5 mr-1.5" /> Copiar
                      </Button>
                    </div>
                    <CopyRow
                      label="Monto exacto (USD)"
                      value={`$${order.total.toFixed(2)}`}
                      onCopy={() => copy(order.total.toFixed(2), "Monto")}
                      testid="zelle-amount"
                      big
                    />
                    <CopyRow
                      label="Nota de referencia (opcional)"
                      value={order.name}
                      onCopy={() => copy(order.name, "Referencia")}
                      testid="zelle-reference"
                    />

                    <button
                      type="button"
                      onClick={downloadZelleContact}
                      data-testid="add-zelle-contact-btn"
                      className="w-full flex items-center justify-center gap-2 rounded-lg border border-yellow-400/40 bg-yellow-400/5 hover:bg-yellow-400/10 active:bg-yellow-400/15 text-yellow-200 font-semibold text-sm py-3 transition-colors"
                    >
                      <UserPlus className="w-4 h-4" />
                      Guardar contacto Zelle en mi teléfono
                    </button>
                    <p className="text-[11px] text-zinc-500 -mt-2 text-center px-2 leading-relaxed">
                      Descarga un archivo <span className="font-mono">.vcf</span> con el nombre y email Zelle.
                      Ábrelo desde tu móvil y confirma para agregarlo a tus contactos.
                    </p>
                  </div>
                </>
              )}

              <div className="mt-8 flex items-center gap-3 text-sm text-zinc-500">
                <Loader2 className="w-4 h-4 animate-spin text-yellow-400" />
                <span className="font-mono-display tracking-wider uppercase text-xs">
                  Verificando pago en tiempo real...
                </span>
              </div>
            </div>
          </motion.section>

          {/* RIGHT: Summary */}
          <aside className="md:col-span-2 space-y-4">
            <div className="border border-white/10 bg-zinc-900 rounded-xl p-6">
              <div className="text-[11px] uppercase tracking-[0.3em] font-mono-display text-zinc-500 mb-4">
                Resumen de la orden
              </div>
              <SumRow label="Cliente" value={order.name} />
              <SumRow label="Email" value={order.email} />
              <SumRow label="Teléfono" value={order.phone} />
              <SumRow
                label="Combustible"
                value={
                  order.fuel_label
                    ? `${order.fuel_label}${order.fuel_octane ? " · " + order.fuel_octane : ""}`
                    : "Gasolina B94"
                }
              />
              <SumRow label="Litros" value={(order.liters ?? order.gallons ?? 0).toString()} />
              <SumRow label="Precio/litro" value={`$${(order.price_per_liter ?? order.price_per_gallon ?? 0).toFixed(2)}`} />
              <SumRow label="Subtotal" value={`$${(order.subtotal ?? order.total).toFixed(2)}`} />
              {order.fee > 0 && (
                <SumRow label="Fee tarjeta" value={`$${order.fee.toFixed(2)}`} />
              )}
              <div className="border-t border-white/10 mt-4 pt-4 flex items-baseline justify-between">
                <span className="text-xs uppercase tracking-widest text-zinc-500 font-mono-display">Total</span>
                <span className="font-display font-black text-3xl text-yellow-400" data-testid="summary-total">
                  ${order.total.toFixed(2)}
                </span>
              </div>
            </div>

            <div className="border border-white/10 bg-zinc-900 rounded-xl p-6 text-sm text-zinc-400">
              <div className="flex items-center gap-2 mb-2 text-white font-semibold">
                <CheckCircle2 className="w-4 h-4 text-emerald-400" /> ¿Cómo funciona?
              </div>
              <ol className="space-y-2 list-decimal list-inside">
                {isStripe ? (
                  <>
                    <li>Completa tu pago con tarjeta en la ventana de Stripe.</li>
                    <li>Stripe confirma el pago automáticamente.</li>
                    <li>Recibirás el código de <b>6 letras</b> aquí mismo.</li>
                  </>
                ) : (
                  <>
                    <li>Envía el monto exacto por Zelle al correo mostrado.</li>
                    <li>El sistema detecta tu pago automáticamente.</li>
                    <li>Recibirás un código de <b>6 letras</b> para retirar en la estación.</li>
                  </>
                )}
              </ol>
            </div>

            <p className="text-center text-xs text-zinc-600 font-mono-display tracking-widest">
              ID: {order.id.slice(0, 8).toUpperCase()}
            </p>
          </aside>
        </div>
      </main>
      <Footer />
    </div>
  );
}

function CopyRow({ label, value, onCopy, testid, big = false }) {
  return (
    <div className="border border-white/10 bg-zinc-900/60 rounded-lg p-4 flex items-center justify-between gap-3">
      <div className="min-w-0">
        <div className="text-[11px] uppercase tracking-[0.25em] text-zinc-500 font-mono-display mb-1">
          {label}
        </div>
        <div
          className={`text-white font-mono-display truncate ${big ? "text-xl md:text-2xl font-bold" : "text-base"}`}
          data-testid={`${testid}-value`}
        >
          {value}
        </div>
      </div>
      <button
        onClick={onCopy}
        data-testid={`${testid}-copy-btn`}
        className="shrink-0 flex items-center gap-1.5 px-3 py-2 rounded-md bg-yellow-400 hover:bg-yellow-300 text-black text-xs font-bold transition-colors"
      >
        <Copy className="w-3.5 h-3.5" /> Copiar
      </button>
    </div>
  );
}

function SumRow({ label, value }) {
  return (
    <div className="flex items-center justify-between py-1.5 text-sm">
      <span className="text-zinc-500">{label}</span>
      <span className="text-white font-medium truncate max-w-[60%] text-right">{value}</span>
    </div>
  );
}
